// src/cockpit4/components/OpsMonitoring.tsx
// ============================================
// 运维监控（设置中心 P2-功能9）- v2.0 详细改进计划
// 系统性能监控 / 业务指标监控 / 告警通知 / 容量规划 / 日志管理 / 健康检查
// 数据来源：localStorage（xeaos_ops_monitoring）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Cpu, MemoryStick, HardDrive, Bell, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const CPU_DATA = [
  { time: "00:00", cpu: 32, mem: 45 },
  { time: "04:00", cpu: 28, mem: 42 },
  { time: "08:00", cpu: 58, mem: 55 },
  { time: "12:00", cpu: 72, mem: 63 },
  { time: "16:00", cpu: 65, mem: 60 },
  { time: "20:00", cpu: 48, mem: 52 },
];

export default function OpsMonitoring() {
  const [refreshing, setRefreshing] = useState(false);

  const metrics = useMemo(() => [
    { label: "CPU 使用率", value: 65, icon: Cpu, color: "#38BDF8", status: "正常" },
    { label: "内存使用", value: 58, icon: MemoryStick, color: "#A78BFA", status: "正常" },
    { label: "磁盘占用", value: 42, icon: HardDrive, color: "#34D399", status: "正常" },
    { label: "服务健康度", value: 98, icon: Activity, color: "#34D399", status: "优秀" },
  ], []);

  const onRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 800);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Activity size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">运维监控</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Ops Monitoring</span>
        <button onClick={onRefresh} className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-400 hover:text-white">
          <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} /> 刷新
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {metrics.map((m) => (
          <motion.div key={m.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
            <div className="flex items-center gap-1.5 text-[9px] font-mono text-gray-500 uppercase">
              <m.icon size={10} style={{ color: m.color }} /> {m.label}
            </div>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-semibold font-mono" style={{ color: m.color }}>{m.value}%</span>
              <span className="text-[9px] font-mono text-gray-500">{m.status}</span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Cpu size={11} className="text-sky-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">服务器性能 · 24 小时</span>
          </div>
          <div className="h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={CPU_DATA} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="time" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={32} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} />
                <Line type="monotone" dataKey="cpu" name="CPU" stroke="#38BDF8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="mem" name="内存" stroke="#A78BFA" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">告警与容量规划</span>
          </div>
          <div className="space-y-1.5 text-[9px] font-mono">
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400">✅ 全部 5 个服务健康 · 响应时间 p99 320ms</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-amber-400">⚠ 磁盘使用 42%，按当前增速预计 90 天后需扩容</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400">📊 业务指标：今日 API 调用 8,400 次 · 错误率 0.2% · 无告警</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400">🗂 日志：日滚动归档 · 保留 30 天 · 全文检索已启用</div>
          </div>
        </div>
      </div>
    </div>
  );
}
